import { format, formatDistanceToNow } from 'date-fns';

export const LEAD_STATUS_META = {
	ready: { label: 'Ready to send', color: 'blue' },
	emailed: { label: 'Emailed', color: 'purple' },
	follow_up_1: { label: 'Follow-up 1 sent', color: 'purple' },
	follow_up_2: { label: 'Follow-up 2 sent', color: 'purple' },
	bounced: { label: 'Bounced', color: 'orange' },
	replied: { label: 'Replied', color: 'teal' },
	walkthrough: { label: 'Walkthrough', color: 'cyan' },
	review_proposed: { label: 'Review proposed', color: 'orange' },
	won: { label: 'Won', color: 'green' },
	lost: { label: 'Lost', color: 'red' },
	not_interested: { label: 'Not interested', color: 'gray' },
};

export const LEAD_STATUSES = Object.keys(LEAD_STATUS_META);
/** Terminal outcomes. Bounced stays “open” so it remains on the tracker until you fix the contact or close it. */
export const CLOSED_LEAD_STATUSES = ['won', 'lost', 'not_interested'];
export const AWAITING_REPLY_STATUSES = [
	'emailed',
	'follow_up_1',
	'follow_up_2',
];
export const IN_CONVERSATION_STATUSES = [
	'replied',
	'walkthrough',
	'review_proposed',
];

export const EVENT_LABELS = {
	emailed: 'First email sent',
	follow_up_1: 'Follow-up 1 sent',
	follow_up_2: 'Follow-up 2 sent',
	bounced: 'Bounced',
	call: 'Phone call',
	linkedin: 'LinkedIn touch',
	replied: 'They replied',
	walkthrough: 'Walkthrough booked',
	review_proposed: 'Operations Review proposed',
	won: 'Won',
	lost: 'Lost',
	not_interested: 'Not interested',
	note: 'Note',
	status: 'Status changed',
	imported: 'Imported',
	created: 'Created',
};

/** Event types offered in the Log menu, with a divider before the index in LOG_MENU_DIVIDERS. */
export const LOGGABLE_EVENTS = [
	'emailed',
	'follow_up_1',
	'follow_up_2',
	'bounced',
	'call',
	'linkedin',
	'replied',
	'walkthrough',
	'review_proposed',
	'won',
	'lost',
	'not_interested',
];
export const LOG_MENU_DIVIDERS = [4, 6, 9];

/** The send step that follows each status, offered as the main button. */
export const NEXT_SEND = {
	ready: 'emailed',
	emailed: 'follow_up_1',
	follow_up_1: 'follow_up_2',
};

export const CONTACT_TARGETS = [
	'Primary',
	'Backup',
	'Secondary',
	'Senior',
	'Approver',
	'Escalation',
	'Press route',
	'Generic',
	'Drop',
];
export const CONTACT_CONFIDENCE = ['High', 'Medium', 'Medium-Low', 'Low'];

export const confidenceColor = (c = '') =>
	/^high/i.test(c)
		? 'green'
		: /^medium/i.test(c)
			? 'yellow'
			: /^low/i.test(c)
				? 'orange'
				: 'gray';

export const targetColor = (t = '') =>
	/^primary/i.test(t)
		? 'green'
		: /^backup/i.test(t)
			? 'blue'
			: /approver|escalation|senior/i.test(t)
				? 'purple'
				: 'gray';

export const isDropped = (contact) => /^drop/i.test(contact?.target || '');

const VERIFICATION_META = {
	valid: { label: 'Verified', color: 'green' },
	invalid: { label: 'Invalid', color: 'red' },
	disposable: { label: 'Disposable', color: 'red' },
	risky: { label: 'Risky', color: 'orange' },
	catch_all: { label: 'Catch-all', color: 'orange' },
	unknown: { label: 'Unknown', color: 'gray' },
};

/** The contact's last email check, or null if never checked or the email has since changed. */
export const emailVerificationOf = (contact) => {
	const v = contact?.emailVerification;
	if (!v?.status || !contact.email) return null;
	if ((v.email || '').toLowerCase() !== contact.email.toLowerCase()) return null;
	return { ...v, ...(VERIFICATION_META[v.status] || VERIFICATION_META.unknown) };
};

export const primaryOf = (lead) =>
	lead?.contacts?.find((c) => c._id === lead.primaryContactId) ?? null;

export const isClosed = (lead) => CLOSED_LEAD_STATUSES.includes(lead.status);

export const endOfToday = () => {
	const d = new Date();
	d.setHours(23, 59, 59, 999);
	return d;
};

/** Open lead whose next step is due today or earlier. */
export const isDue = (lead, until = endOfToday()) =>
	!!lead.nextActionAt && !isClosed(lead) && new Date(lead.nextActionAt) <= until;

export const isOverdue = (lead) => {
	if (!lead.nextActionAt || isClosed(lead)) return false;
	const startOfToday = new Date();
	startOfToday.setHours(0, 0, 0, 0);
	return new Date(lead.nextActionAt) < startOfToday;
};

export const fmtDate = (value) =>
	value ? format(new Date(value), 'd MMM yyyy') : '';
export const fmtDayShort = (value) =>
	value ? format(new Date(value), 'EEE d MMM') : '';
export const fmtDateTime = (value) =>
	value ? format(new Date(value), 'd MMM yyyy, HH:mm') : '';
export const timeAgo = (value) =>
	value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : '';

/** Date → yyyy-mm-dd in local time, for <input type="date">. */
export const toInputDate = (value) =>
	value ? format(new Date(value), 'yyyy-MM-dd') : '';

/** yyyy-mm-dd → ISO string at 09:00 local time. */
export const fromInputDate = (value) =>
	value ? new Date(`${value}T09:00:00`).toISOString() : null;

export const mailtoLink = (to, subject, body) =>
	`mailto:${encodeURIComponent((to || '').trim())}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`;

export const userName = (user) =>
	user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '';

/** 17834974 → "£17.8m", 450000 → "£450k" */
export const fmtMoney = (n) => {
	if (n === undefined || n === null || Number.isNaN(Number(n))) return '';
	const abs = Math.abs(n);
	if (abs >= 1e9) return `£${+(n / 1e9).toFixed(1)}bn`;
	if (abs >= 1e6) return `£${+(n / 1e6).toFixed(1)}m`;
	if (abs >= 1e3) return `£${Math.round(n / 1e3)}k`;
	return `£${n}`;
};

const ESTIMATE_RANGE =
	/£\s*([\d.]+)\s*([kmb]n?)?\s*[-–]\s*£\s*([\d.]+)\s*([kmb]n?)?/i;
const scaleOf = (unit = '') =>
	({ k: 1e3, m: 1e6, b: 1e9, bn: 1e9 })[unit.toLowerCase()] ?? 1;

/** What to show for revenue: the filed figure, else the estimated range, else why it's unknown. */
export const revenueLabel = (lead) => {
	const note = lead.revenueNote || '';
	if (typeof lead.revenue === 'number')
		return { text: fmtMoney(lead.revenue), hint: note, estimated: false };
	const range = note.match(ESTIMATE_RANGE);
	if (range)
		return {
			text: `£${range[1]}${range[2] || ''}–£${range[3]}${range[4] || ''}`,
			hint: note,
			estimated: true,
		};
	return { text: '', hint: note, estimated: false };
};

/** "£201.8m · Filed accounts", "£5m–£10m (estimate)" or "Not disclosed (UK arm is small)" */
export const revenueDescription = ({ text, hint, estimated }) => {
	if (!text) return hint;
	if (estimated) return `${text} (estimate)`;
	return hint ? `${text} · ${hint}` : text;
};

/** Filed revenue, or the middle of an estimated range, for sorting. */
export const revenueSortValue = (lead) => {
	if (typeof lead.revenue === 'number') return lead.revenue;
	const range = (lead.revenueNote || '').match(ESTIMATE_RANGE);
	if (!range) return null;
	const low = Number(range[1]) * scaleOf(range[2] || range[4]);
	const high = Number(range[3]) * scaleOf(range[4]);
	return (low + high) / 2;
};

const PRIORITY_COLORS = {
	a: 'green',
	high: 'green',
	b: 'purple',
	c: 'blue',
	medium: 'blue',
	d: 'gray',
	low: 'gray',
	e: 'gray',
	x: 'red',
};

export const priorityColor = (p = '') =>
	PRIORITY_COLORS[p.trim().toLowerCase()] ?? 'gray';

const QUALITY_COLORS = {
	a: 'green',
	b: 'teal',
	c: 'yellow',
	d: 'orange',
	e: 'red',
	f: 'red',
};

export const QUALITY_GRADES = ['A', 'B', 'C', 'D'];

export const qualityColor = (grade = '') =>
	QUALITY_COLORS[grade.trim().toLowerCase()] ?? 'gray';

export const gradeFromScore = (score) =>
	score >= 75 ? 'A' : score >= 60 ? 'B' : score >= 45 ? 'C' : 'D';

/** The lead's research quality as { grade, score, reasons }, or null when it has none. */
export const qualityOf = (lead) => {
	const q = lead?.quality;
	const hasScore = typeof q?.score === 'number';
	if (!q?.grade && !hasScore) return null;
	return {
		grade: q.grade || gradeFromScore(q.score),
		score: hasScore ? q.score : null,
		reasons: q.reasons || [],
	};
};

/** "trades-plumbing-heating" → "trades plumbing heating" */
export const tagLabel = (tag = '') => tag.replace(/[-_]+/g, ' ');

/** "Priority C · Mid-size (est. £1m-£10m) · Owner: Aury Silva" */
export const leadSubtitle = (lead) =>
	[
		lead.priority ? `Priority ${lead.priority}` : '',
		lead.tier,
		lead.owner ? `Owner: ${userName(lead.owner)}` : '',
	]
		.filter(Boolean)
		.join(' · ');
